import { useEffect, useRef, useState } from "react";
import {ChevronDown, ArrowRight} from 'lucide-react';

const stats = [
  { value: "+500", label: "Produtos em Estoque" },
  { value: "100%", label: "Atendimento Técnico" },
  { value: "+10",  label: "Anos de Experiência" },
];

const slides = [
  {
    image: "https://static.lumi.new/c1/c13ab7a5ba44b6df0753e83779459dc2.png",
    tag:   "Mangueiras · Borrachas · Acessórios",
    title: "Soluções",
    titleGold: "Hidráulicas",
    sub:   "de Alta Qualidade",
    desc:  "Especialistas em mangueiras hidráulicas, conexões, terminais e soluções industriais para todos os segmentos do mercado.",
  },
  {
    image: "https://static.lumi.new/ad/ad55358fc4cfc0e0091703d3b33139be.png",
    tag:   "Indústria · Petróleo · Construção",
    title: "Presentes nos",
    titleGold: "Maiores Projetos",
    sub:   "Industriais",
    desc:  "Da refinaria à construção civil, fornecemos conexões e mangueiras de alta pressão para as operações mais exigentes do mercado.",
  },
];

const INTERVAL = 6000;

export default function Hero() {
  const titleRef  = useRef<HTMLHeadingElement>(null);
  const [visible, setVisible] = useState(false);
  const [current, setCurrent] = useState(0);
  const [fading,  setFading]  = useState(false);

  /* entrada inicial */
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 150);
    return () => clearTimeout(t);
  }, []);

  /* animação do título */
  useEffect(() => {
    const el = titleRef.current;
    if (!el) return;
    el.style.opacity   = "0";
    el.style.transform = "translateY(36px)";
    const t = setTimeout(() => {
      el.style.transition = "opacity 0.9s ease, transform 0.9s ease";
      el.style.opacity    = "1";
      el.style.transform  = "translateY(0)";
    }, 300);
    return () => clearTimeout(t);
  }, []);

  /* slider automático */
  useEffect(() => {
    const id = setInterval(() => {
      setFading(true);
      setTimeout(() => {
        setCurrent(prev => (prev + 1) % slides.length);
        setFading(false);
      }, 600);
    }, INTERVAL);
    return () => clearInterval(id);
  }, []);

  const goTo = (idx: number) => {
    if (idx === current) return;
    setFading(true);
    setTimeout(() => { setCurrent(idx); setFading(false); }, 600);
  };

  const scrollTo = (id: string) =>
    document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });

  const slide = slides[current];

  return (
    <section
      id="inicio"
      className="relative min-h-screen flex items-center overflow-hidden bg-[#0D1B3E]"
    >
      {/* ── Background slides ── */}
      {slides.map((s, i) => (
        <div
          key={i}
          className="absolute inset-0 bg-cover bg-center bg-no-repeat transition-opacity duration-700"
          style={{
            backgroundImage: `url('${s.image}')`,
            opacity: i === current ? (fading ? 0 : 0.45) : 0,
          }}
        />
      ))}

      {/* ── Gradientes ── */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#0D1B3E] via-[#0D1B3E]/85 to-[#0D1B3E]/30" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0D1B3E] via-transparent to-[#0D1B3E]/20" />

      {/* ── Ambient glow ── */}
      <div className="absolute -left-32 top-1/3 w-[28rem] h-[28rem] bg-[#F5A623]/8 rounded-full blur-[100px] pointer-events-none" />
      <div className="absolute right-0 bottom-0 w-[22rem] h-[22rem] bg-[#F5A623]/5 rounded-full blur-[80px] pointer-events-none" />

      {/* ── Gold vertical rule ── */}
      <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-transparent via-[#F5A623] to-transparent opacity-80" />

      {/* ── Conteúdo ── */}
      <div className="relative z-10 w-full px-6 md:px-10 lg:px-14 py-28 flex flex-col items-start">

        {/* Kicker badge */}
        <div
          className={`transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
          style={{ transitionDelay: "100ms" }}
        >
          <span
            className={`inline-flex items-center gap-2.5 bg-[#F5A623]/12 border border-[#F5A623]/25 text-[#F5A623] text-xs font-black uppercase tracking-[0.15em] px-4 py-2 rounded-full mb-8 backdrop-blur-sm transition-opacity duration-600 ${fading ? "opacity-0" : "opacity-100"}`}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#F5A623] animate-pulse" />
            {slide.tag}
          </span>
        </div>

        {/* Heading */}
        <h1
          ref={titleRef}
          className={`font-barlow-condensed text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-black text-white uppercase leading-[0.9] max-w-3xl transition-opacity duration-600 ${fading ? "opacity-0" : "opacity-100"}`}
        >
          <span className="text-white">{slide.title} </span><span className="text-[#F5A623]">{slide.titleGold}</span>
          <br />
          <span className="text-white/70 text-2xl md:text-3xl lg:text-4xl font-bold leading-none">
            {slide.sub}
          </span>
        </h1>

        {/* Desc */}
        <div
          className={`transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
          style={{ transitionDelay: "400ms" }}
        >
          <p
            className={`mt-8 text-gray-300/85 text-base md:text-lg max-w-xl leading-relaxed font-light transition-opacity duration-600 ${fading ? "opacity-0" : "opacity-100"}`}
          >
            {slide.desc}
          </p>
        </div>

        {/* CTAs */}
        <div
          className={`mt-10 flex flex-col sm:flex-row gap-4 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
          style={{ transitionDelay: "550ms" }}
        >
          <a
            href="#produtos"
            onClick={e => { e.preventDefault(); scrollTo("#produtos"); }}
            className="group inline-flex items-center gap-2 bg-[#F5A623] hover:bg-[#ffc048] text-[#0D1B3E] font-black px-9 py-4 rounded-xl text-sm uppercase tracking-widest transition-all duration-300 hover:-translate-y-1 shadow-2xl shadow-[#F5A623]/30 hover:shadow-[#F5A623]/50"
          >
            Ver Catálogo
            <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </a>
          <a
            href="#contato"
            onClick={e => { e.preventDefault(); scrollTo("#contato"); }}
            className="inline-flex items-center gap-2 border-2 border-white/40 hover:border-white text-white hover:bg-white hover:text-[#0D1B3E] font-bold px-9 py-4 rounded-xl text-sm uppercase tracking-widest transition-all duration-300 backdrop-blur-sm"
          >
            Fale Conosco
          </a>
        </div>

        {/* Stats */}
        <div
          className={`mt-16 pt-8 border-t border-white/10 flex flex-wrap gap-x-12 gap-y-6 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
          style={{ transitionDelay: "700ms" }}
        >
          {stats.map(({ value, label }, i) => (
            <div key={label} className={`flex flex-col ${i > 0 ? "sm:pl-12 sm:border-l border-white/10" : ""}`}>
              <span className="text-[#F5A623] text-3xl md:text-4xl font-black font-barlow-condensed leading-none tracking-tight">
                {value}
              </span>
              <span className="text-gray-400 text-xs font-semibold mt-2 uppercase tracking-widest">
                {label}
              </span>
            </div>
          ))}
        </div>

        {/* ── Indicadores de slide ── */}
        <div
          className={`mt-10 flex items-center gap-3 transition-all duration-700 ${visible ? "opacity-100" : "opacity-0"}`}
          style={{ transitionDelay: "800ms" }}
        >
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              aria-label={`Slide ${i + 1}`}
              className={`rounded-full transition-all duration-400 ${
                i === current
                  ? "w-8 h-2.5 bg-[#F5A623]"
                  : "w-2.5 h-2.5 bg-white/30 hover:bg-white/60"
              }`}
            />
          ))}
          {/* barra de progresso */}
          <div className="ml-2 w-20 h-0.5 bg-white/10 rounded-full overflow-hidden">
            <div
              key={current}
              className="h-full bg-[#F5A623] rounded-full"
              style={{ animation: `progressBar ${INTERVAL}ms linear forwards` }}
            />
          </div>
        </div>
      </div>

      {/* ── Scroll indicator ── */}
      <button
        onClick={() => scrollTo("#empresa")}
        className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 text-white/40 hover:text-[#F5A623] transition-colors"
        aria-label="Scroll para baixo"
      >
        <span className="text-[10px] uppercase tracking-[0.2em] font-semibold">Scroll</span>
        <ChevronDown size={22} className="animate-bounce" />
      </button>
    </section>
  );
}
